import React, { FC } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material'

import { useTranslate } from '../../../hooks/shared'

const useStyles = makeStyles<void, 'checked' | 'label'>()((theme: Theme, _params, classes) => ({
  root: {
    border: `1px solid ${theme.palette.primary.main}`,
    position: 'relative',
    margin: 0,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.primary.dark,
    textTransform: 'uppercase',
    ...theme.brand.assets.layout?.customLabelRadio,
    fontSize: '1rem',
    '&:first-child': {
      borderRight: 'none',
    },
    '&.checked': {
      background: theme.brand.colors.main,
    },
  },

  label: {
    width: '100%',
    textAlign: 'center',
    fontSize: '1rem',
    position: 'absolute',
    color: theme.palette.primary.dark,
    ...theme.brand.typography.overrides?.ownerRadioOverride,
  },
  legend: {
    fontSize: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  control: {
    paddingBottom: theme.spacing(2),
  },
  inputRadio: {
    borderRadius: 0,
    width: '70px',
    height: '30px',
    backgroundColor: theme.palette.primary.contrastText,
    ...theme.brand.assets.layout?.customInputRadio,
    padding: '5px',
    [`&.${classes.checked}`]: {
      color: 'red',
    },
  },
  icon: {
    borderRadius: 0,
  },
  checkedIcon: {
    borderRadius: 0,
  },
  checked: {
    backgroundColor: theme.palette.primary.main,
    '&:hover ': {
      backgroundColor: `${theme.brand.colors.main} !important`,
    },
    [`&, & + .${classes.label}`]: {
      '& > span': {
        BorderRight: 0,
      },
      color: theme.palette.primary.contrastText,
      ...theme.brand.assets.layout?.checkedLabel,
    },
  },
}))

interface RadioOption {
  value: string
  text: string
}

interface RFFFormControlRadioProps extends FieldRenderProps<unknown> {
  fieldLabel: string
  options: {
    yes: RadioOption
    no: RadioOption
  }
  testId: string
}

const RFFFormControlRadio: FC<RFFFormControlRadioProps> = ({
  input,
  meta,
  fieldLabel,
  testId,
  options,
}) => {
  const { t } = useTranslate()
  const { classes, cx } = useStyles()
  const isError: boolean = (!!meta.error || meta.submitError) && !meta.pristine

  return (
    <FormControl
      variant='standard'
      id={testId}
      component='fieldset'
      className={classes.control}
      error={isError}
    >
      <FormLabel component='legend' className={classes.legend}>
        {fieldLabel}
      </FormLabel>

      {/* `name` comes from the field, not from MUI. Left unset, RadioGroup falls back to
          `unstable_useId`, which assigns a random `mui-NNNNN` in a MOUNT EFFECT — so the
          group had no stable identity until a commit after it first rendered. */}
      <RadioGroup
        name={input.name}
        onChange={input.onChange}
        value={input.value}
        aria-label={fieldLabel}
        row
      >
        {[options.yes, options.no].map((option) => (
          <FormControlLabel
            key={option.value}
            // Derived from the field, not from local state: a value set by anything other
            // than a click (initialValues, form.change) has to highlight too.
            classes={{
              root: cx(classes.root, { checked: input.value === option.value }),
              label: classes.label,
            }}
            label={t(option.text)}
            value={option.value}
            control={
              <Radio
                id={`${testId}_${option.text}`}
                classes={{ root: classes.inputRadio, checked: classes.checked }}
                checkedIcon={<span className={cx(classes.icon, classes.checkedIcon)} />}
                icon={<span className={classes.icon} />}
              />
            }
          />
        ))}
      </RadioGroup>
      {isError && <FormHelperText>{meta.error}</FormHelperText>}
    </FormControl>
  )
}

export default RFFFormControlRadio
